import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Tag, message, Modal, Form, Input, Select, Row, Col, Typography,
  Space, Badge, Empty, Spin, Tabs, Avatar, Statistic, Tooltip, Divider
} from 'antd';
import { 
  CheckCircleOutlined, ExclamationCircleOutlined, BugOutlined, 
  CommentOutlined, SearchOutlined, ReloadOutlined, FilterOutlined,
  PieChartOutlined, BarChartOutlined, UserOutlined, ClockCircleOutlined,
  FireOutlined, SolutionOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import Chart from 'react-apexcharts';

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const Issues = () => {
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [resolveModalVisible, setResolveModalVisible] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [adminComment, setAdminComment] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  // Metrics
  const [metrics, setMetrics] = useState({
    totalIssues: 0,
    pendingIssues: 0,
    solvedIssues: 0,
    criticalIssues: 0
  });

  // Chart states
  const [issuesByTypeData, setIssuesByTypeData] = useState({
    labels: [],
    series: []
  });

  const [issuesByStatusData, setIssuesByStatusData] = useState({
    categories: ['Pending', 'In Progress', 'Resolved'],
    series: [{
      name: 'Issues',
      data: [0, 0, 0]
    }]
  });

  useEffect(() => {
    fetchIssues();
  }, []);

  useEffect(() => {
    if (issues.length > 0) {
      calculateMetrics();
      prepareChartData();
      applyFilters();
    }
  }, [issues, searchText, filterType, filterStatus, activeTab]);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllHelp', {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const issuesData = await response.json();
      
      // Extract help requests from the nested structure
      let allIssues = [];
      issuesData.forEach(userGroup => {
        if (userGroup.helpRequests && Array.isArray(userGroup.helpRequests)) {
          const userIssues = userGroup.helpRequests.map(issue => ({
            ...issue,
            user: userGroup.user, // Add user info to each issue item
            priority: getPriorityFromType(issue.issue_type),
            solved: issue.status === 'resolved'
          }));
          allIssues = allIssues.concat(userIssues);
        }
      });
      
      setIssues(allIssues);
      setFilteredIssues(allIssues);
    } catch (error) {
      console.error('Failed to fetch issues:', error);
      message.error('Failed to fetch issues');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityFromType = (type) => {
    if (type === 'Refund Issue') return 'high';
    if (type === 'App Crash') return 'critical';
    return 'medium';
  };

  const calculateMetrics = () => {
    const totalIssues = issues.length;
    const pendingIssues = issues.filter(issue => !issue.solved).length;
    const solvedIssues = issues.filter(issue => issue.solved).length;
    const criticalIssues = issues.filter(issue => issue.priority === 'critical' && !issue.solved).length;

    setMetrics({
      totalIssues,
      pendingIssues,
      solvedIssues,
      criticalIssues
    });
  };

  const prepareChartData = () => {
    // Issues by type chart
    const typeCount = {};
    issues.forEach(issue => {
      const type = issue.issue_type || 'Other';
      typeCount[type] = (typeCount[type] || 0) + 1;
    });

    setIssuesByTypeData({
      labels: Object.keys(typeCount),
      series: Object.values(typeCount)
    });

    // Issues by status chart
    const pending = issues.filter(issue => issue.status === 'pending').length;
    const inProgress = issues.filter(issue => issue.status === 'in_progress').length;
    const resolved = issues.filter(issue => issue.solved).length;

    setIssuesByStatusData({
      categories: ['Pending', 'In Progress', 'Resolved'],
      series: [{
        name: 'Issues',
        data: [pending, inProgress, resolved]
      }]
    });
  };

  const applyFilters = () => {
    let result = [...issues];
    
    // Apply search filter
    if (searchText) {
      const lowerSearchText = searchText.toLowerCase();
      result = result.filter(issue => 
        (issue.description && issue.description.toLowerCase().includes(lowerSearchText)) ||
        (issue.issue_type && issue.issue_type.toLowerCase().includes(lowerSearchText))
      );
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      result = result.filter(issue => issue.issue_type === filterType);
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'solved') {
        result = result.filter(issue => issue.solved);
      } else if (filterStatus === 'pending') {
        result = result.filter(issue => !issue.solved);
      }
    }

    // Apply tab filter
    if (activeTab === 'critical') {
      result = result.filter(issue => issue.priority === 'critical');
    } else if (activeTab === 'pending') {
      result = result.filter(issue => !issue.solved);
    } else if (activeTab === 'solved') {
      result = result.filter(issue => issue.solved);
    }
    
    setFilteredIssues(result);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleTypeFilterChange = (value) => {
    setFilterType(value);
  };

  const handleStatusFilterChange = (value) => {
    setFilterStatus(value);
  };

  const resetFilters = () => {
    setSearchText('');
    setFilterType('all');
    setFilterStatus('all');
  };

  const handleMarkAsSolved = async (issueId, adminComment) => {
    try {
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/resolveHelpRequest', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          helpRequestId: issueId,
          adminComment: adminComment,
          adminId: localStorage.getItem('userUid') || 'admin-001' // Use logged in user UID
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Update local state
      const updatedIssues = issues.map(issue => 
        issue.id === issueId ? { 
          ...issue, 
          solved: true, 
          status: 'resolved',
          admin_comment: adminComment,
          resolved_at: new Date().toISOString()
        } : issue
      );
      setIssues(updatedIssues);
      message.success('Issue marked as solved');
    } catch (error) {
      console.error('Failed to update issue status:', error);
      message.error('Failed to update issue status');
    }
  };

  const showResolveModal = (issue) => {
    setSelectedIssue(issue);
    setAdminComment('');
    setResolveModalVisible(true);
  };

  const handleResolveSubmit = () => {
    if (!adminComment.trim()) {
      message.error('Please enter an admin comment');
      return;
    }
    handleMarkAsSolved(selectedIssue.id, adminComment.trim());
    setResolveModalVisible(false);
    setSelectedIssue(null);
    setAdminComment('');
  };

  const handlePriorityChange = async (issueId, priority) => {
    try {
      // In a real implementation, you would call your API here
      // For demo purposes, we'll just update the local state
      const updatedIssues = issues.map(issue => 
        issue.id === issueId ? { ...issue, priority } : issue
      );
      setIssues(updatedIssues);
      message.success('Issue priority updated');
    } catch (error) {
      message.error('Failed to update issue priority');
    }
  };

  const getIssueTypeColor = (type) => {
    const colors = {
      'Refund Issue': 'red',
      'App Crash': 'orange',
      'Other issues': 'blue',
      'Payment Issue': 'purple',
      'Account Issue': 'cyan'
    };
    return colors[type] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'blue',
      'medium': 'orange',
      'high': 'red',
      'critical': 'magenta'
    };
    return colors[priority] || 'default';
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      'low': <Badge status="processing" />,
      'medium': <Badge status="warning" />,
      'high': <ExclamationCircleOutlined style={{ color: 'var(--danger-color)' }} />,
      'critical': <FireOutlined style={{ color: 'var(--danger-color)' }} />
    };
    return icons[priority] || <Badge status="default" />;
  };

  const getIssueTypes = () => {
    const types = new Set(issues.map(issue => issue.issue_type).filter(Boolean));
    return ['all', ...types];
  };

  // Chart options
  const donutOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    legend: {
      position: 'bottom'
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--danger-color)', 'var(--warning-color)', 'var(--primary-color)', 'var(--accent-color)', 'var(--secondary-color)'],
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 600
            },
            total: {
              show: true,
              label: 'Total',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      }
    }
  };

  const barOptions = {
    chart: {
      type: 'bar',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: '60%'
      },
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)'],
    yaxis: {
      title: {
        text: 'Number of Issues'
      }
    },
    fill: {
      opacity: 1,
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: "vertical",
        shadeIntensity: 0.25,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 0.85,
        stops: [50, 100],
      },
    },
  };

  if (loading && issues.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '2rem' }}>
        <Spin size="large" tip="Loading issue data..." spinning={true} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: 'var(--background-primary)', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3} style={{ color: 'var(--primary-color)', margin: 0 }}>
          <BugOutlined /> Issue Management
        </Title>
        <Text type="secondary">
          Track and resolve user-reported issues and bugs
        </Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card primary">
            <div>
              <div className="stat-value">{metrics.totalIssues}</div>
              <div className="stat-label">Total Issues</div>
            </div>
            <BugOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card warning">
            <div>
              <div className="stat-value">{metrics.pendingIssues}</div>
              <div className="stat-label">Pending Issues</div>
            </div>
            <ClockCircleOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card success">
            <div>
              <div className="stat-value">{metrics.solvedIssues}</div>
              <div className="stat-label">Solved Issues</div>
            </div>
            <CheckCircleOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card accent">
            <div>
              <div className="stat-value">{metrics.criticalIssues}</div>
              <div className="stat-label">Critical Issues</div>
            </div>
            <FireOutlined className="stat-icon" />
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Issues by Type" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            {issuesByTypeData.series.length > 0 ? (
              <Chart
                options={{
                  ...donutOptions,
                  labels: issuesByTypeData.labels
                }}
                series={issuesByTypeData.series}
                type="donut"
                height="280"
              />
            ) : (
              <Empty description="No issue data available" style={{ marginTop: '80px' }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="Issues by Status" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            {issuesByStatusData.series[0].data.some(value => value > 0) ? (
              <Chart
                options={{
                  ...barOptions,
                  xaxis: {
                    categories: issuesByStatusData.categories
                  }
                }}
                series={issuesByStatusData.series}
                type="bar"
                height="280"
              />
            ) : (
              <Empty description="No status data available" style={{ marginTop: '80px' }} />
            )}
          </Card>
        </Col>
      </Row>

      <Card className="dashboard-card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', marginBottom: '16px', gap: '16px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Input
              placeholder="Search in issues"
              value={searchText}
              onChange={handleSearch}
              style={{ width: '250px' }}
              prefix={<SearchOutlined />}
              allowClear
            />
            
            <Select
              value={filterType}
              onChange={handleTypeFilterChange}
              style={{ width: '180px' }}
              placeholder="Filter by type"
            >
              <Option value="all">All Types</Option>
              {getIssueTypes().filter(t => t !== 'all').map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
            
            <Select
              value={filterStatus}
              onChange={handleStatusFilterChange}
              style={{ width: '150px' }}
              placeholder="Filter by status"
            >
              <Option value="all">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="solved">Solved</Option>
            </Select>
            
            <Button 
              icon={<ReloadOutlined />} 
              onClick={resetFilters}
              type="text"
              title="Reset filters"
            >
              Reset
            </Button>
          </div>
          
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchIssues}
          >
            Refresh
          </Button>
            </div>
            
        <Tabs 
          defaultActiveKey="all" 
          onChange={setActiveTab}
          className="issue-tabs"
        >
          <TabPane 
            tab={
              <span>
                <FilterOutlined /> All Issues ({issues.length})
              </span>
            } 
            key="all"
          />
          <TabPane 
            tab={
              <span>
                <FireOutlined /> Critical ({issues.filter(i => i.priority === 'critical').length})
              </span>
            } 
            key="critical"
          />
          <TabPane 
            tab={
              <span>
                <ClockCircleOutlined /> Pending ({issues.filter(i => !i.solved).length})
              </span>
            } 
            key="pending"
          />
          <TabPane 
            tab={
              <span>
                <CheckCircleOutlined /> Solved ({issues.filter(i => i.solved).length})
              </span>
            } 
            key="solved"
          />
        </Tabs>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '24px',
          padding: '16px 0'
        }}>
          {filteredIssues.length > 0 ? (
            filteredIssues.map((issue) => (
              <Card
                key={issue.id}
                className="issue-card"
                hoverable
                title={
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {getPriorityIcon(issue.priority)}
                    <span 
                      style={{ 
                        fontWeight: 'bold', 
                        color: issue.priority === 'critical' ? 'var(--danger-color)' : 'var(--text-primary)'
                      }}
                    >
                      {issue.issue_type || 'Unknown Issue'}
                    </span>
                  </div>
                }
                extra={
                  <Tag color={getIssueTypeColor(issue.issue_type)}>
                    {issue.issue_type}
                  </Tag>
                }
                actions={issue.solved ? [
                  <div className="issue-solved-badge">
                    <CheckCircleOutlined style={{ color: 'var(--success-color)' }} />
                    <span>Solved</span>
                  </div>
                ] : [
                <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => showResolveModal(issue)}
                    style={{ background: 'var(--success-color)', border: 'none' }}
                  >
                    Resolve
                  </Button>,
                  <Select
                    defaultValue={issue.priority}
                    onChange={(value) => handlePriorityChange(issue.id, value)}
                    style={{ width: '120px' }}
                  >
                    <Option value="low">Low</Option>
                    <Option value="medium">Medium</Option>
                    <Option value="high">High</Option>
                    <Option value="critical">Critical</Option>
                  </Select>
                ]}
              >
                <div className="issue-user-info">
                  <Avatar 
                    size="small" 
                    icon={<UserOutlined />} 
                    style={{ backgroundColor: 'var(--primary-color)' }}
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {issue.user?.name || 'Anonymous User'}
                  </Text>
                </div>

                <Paragraph 
                  ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}
                  style={{ marginTop: '12px', marginBottom: '16px' }}
                >
                  {issue.description || 'No description provided'}
                </Paragraph>

                {issue.order_id && (
                  <div style={{ marginBottom: '12px' }}>
                    <Text strong style={{ fontSize: '12px', color: 'var(--primary-color)' }}>Order ID: </Text>
                    <Text style={{ fontSize: '12px' }}>{issue.order_id}</Text>
                  </div>
                )}

                {issue.admin_comment && (
                  <div style={{ 
                    marginBottom: '12px', 
                    padding: '8px', 
                    background: 'var(--background-secondary)', 
                    borderRadius: '4px',
                    borderLeft: '3px solid var(--success-color)'
                  }}>
                    <Text strong style={{ fontSize: '12px', color: 'var(--success-color)' }}>Admin Response:</Text>
                    <Paragraph style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                      {issue.admin_comment}
                    </Paragraph>
                  </div>
                )}

                <div className="issue-meta">
                  <div className="issue-date">
                    <ClockCircleOutlined style={{ fontSize: '12px', marginRight: '4px' }} />
                    <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                      {new Date(issue.created_at).toLocaleString()}
                    </span>
              </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Tag color={getPriorityColor(issue.priority)} style={{ textTransform: 'capitalize' }}>
                      {issue.priority}
                    </Tag>
                    {issue.solved && (
                      <Tag color="green">Resolved</Tag>
                    )}
                  </div>
            </div>
          </Card>
            ))
          ) : (
            <Empty 
              description="No issues match your filters" 
              style={{ gridColumn: '1 / -1', margin: '32px 0' }}
            />
          )}
      </div>
      </Card>

      {/* Resolve Modal */}
      <Modal
        title="Resolve Issue"
        open={resolveModalVisible}
        onOk={handleResolveSubmit}
        onCancel={() => {
           setResolveModalVisible(false);
           setSelectedIssue(null);
           setAdminComment('');
         }}
        okText="Resolve"
        cancelText="Cancel"
        width={600}
      >
        {selectedIssue && (
           <div>
             <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
               <p><strong>User:</strong> {selectedIssue.user?.name} ({selectedIssue.user?.email})</p>
               <p><strong>Type:</strong> {selectedIssue.issue_type}</p>
               <p><strong>Description:</strong> {selectedIssue.description}</p>
               {selectedIssue.order_id && <p><strong>Order ID:</strong> {selectedIssue.order_id}</p>}
               <p><strong>Date:</strong> {new Date(selectedIssue.created_at).toLocaleDateString()}</p>
             </div>
             <div>
               <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Admin Comment:</label>
               <Input.TextArea
                 value={adminComment}
                 onChange={(e) => setAdminComment(e.target.value)}
                 placeholder="Enter your admin comment..."
                 rows={4}
                 style={{ resize: 'none' }}
               />
             </div>
           </div>
         )}
      </Modal>
    </div>
  );
};

export default Issues;